import React, { createContext, useContext, useEffect, useState } from "react";
import { updateAccountProfile, useAccount } from "../firebase/account";

const BibleVersionContext = createContext(null);

/* Every version the header offers, and the sign-up form's Bible preference. */
export const BIBLE_VERSIONS = [
  { id: "NIV", name: "New International Version" },
  { id: "ESV", name: "English Standard Version" },
  { id: "NASB", name: "New American Standard Bible" },
  { id: "KJV", name: "King James Version" },
  { id: "NKJV", name: "New King James Version" },
  { id: "CSB", name: "Christian Standard Bible" },
  { id: "NLT", name: "New Living Translation" },
  { id: "NET", name: "New English Translation" },
  { id: "NRSVCE", name: "New Revised Standard Version Catholic Edition" },
];

function isKnownVersion(version) {
  return BIBLE_VERSIONS.some((entry) => entry.id === version);
}

export const CATHOLIC_BIBLE_VERSIONS = [
  "NABRE",
  "DRA",
  "RSVCE",
  "NRSVCE",
];

export const DEUTEROCANONICAL_BOOKS = [
  "Tobit",
  "Judith",
  "Wisdom",
  "Sirach",
  "Ecclesiasticus",
  "Baruch",
  "1 Maccabees",
  "2 Maccabees",
  "First Maccabees",
  "Second Maccabees",
  "Additions to Esther",
  "Prayer of Azariah",
  "Song of the Three Young Men",
  "Susanna",
  "Bel and the Dragon",
];

export function isCatholicBibleVersion(version = "") {
  return CATHOLIC_BIBLE_VERSIONS.includes(version);
}

export function isDeuterocanonicalReference(reference = "") {
  const normalizedReference = reference.trim().toLowerCase();

  return DEUTEROCANONICAL_BOOKS.some((book) => {
    const normalizedBook = book.toLowerCase();

    return (
      normalizedReference === normalizedBook ||
      normalizedReference.startsWith(`${normalizedBook} `) ||
      normalizedReference.startsWith(`${normalizedBook}:`)
    );
  });
}

export function canOpenVerseReference(reference = "", bibleVersion = "") {
  if (
    isDeuterocanonicalReference(reference) &&
    !isCatholicBibleVersion(bibleVersion)
  ) {
    return false;
  }

  return true;
}

export function getVerseReferenceBlockReason(reference = "", bibleVersion = "") {
  if (
    isDeuterocanonicalReference(reference) &&
    !isCatholicBibleVersion(bibleVersion)
  ) {
    return "Select a Catholic Bible version to open Deuterocanonical references.";
  }

  return "";
}

export function BibleVersionProvider({ children }) {
  const [bibleVersion, setBibleVersion] = useState(() => {
    return localStorage.getItem("bibleVersion") || "NIV";
  });

  useEffect(() => {
    localStorage.setItem("bibleVersion", bibleVersion);
  }, [bibleVersion]);

  /* Signed in, the account's Bible preference is the version in use, on
     every device -- applied when the profile arrives or changes. */
  const account = useAccount();
  const preference = account.profile?.biblePreference;
  useEffect(() => {
    if (isKnownVersion(preference)) setBibleVersion(preference);
  }, [preference]);

  /* And choosing a version while signed in makes it the preference, so the
     header and the account never disagree. */
  const signedIn = account.status === "signedIn";
  function chooseBibleVersion(version) {
    setBibleVersion(version);
    if (signedIn && isKnownVersion(version) && version !== preference) {
      updateAccountProfile({ biblePreference: version }).catch((error) =>
        console.warn("Account: could not save your Bible preference", error)
      );
    }
  }

  return (
    <BibleVersionContext.Provider
      value={{ bibleVersion, setBibleVersion: chooseBibleVersion }}
    >
      {children}
    </BibleVersionContext.Provider>
  );
}

export function useBibleVersion() {
  const context = useContext(BibleVersionContext);

  if (!context) {
    throw new Error("useBibleVersion must be used inside BibleVersionProvider");
  }

  return context;
}